import React from "react";
import { WrapStatus } from "../../types/wrapQuote";
import { WrapEntry } from "./elements/WrapEntry";
import { WrapReview } from "./elements/WrapReview";
import { WrapInProgress } from "./elements/WrapInProgress";
import { WrapCompleted } from "./elements/WrapCompleted";
import { WarningSection } from "./elements/WarningSection";
import { ProgressBar } from "../../components/ProgressBar/ProgressBar";
import { Spacer } from "../../components/general/Spacer/Spacer";
import { WrapActionButton } from "./elements/WrapActionButton";
import { GlobalStore } from "../../store/global/GlobalStore";
import { Token } from "../../store/global/TokenEnum";
import { getWrappedSymbolForChain } from "../../utils/chainUtils";
import { NetworkDropdown } from "../../components/NetworkDropdown/NetworkDropdown";
import { useUpdateAddressWithENS } from "../../hooks/useUpdateAddressWithENS";
import { WrapHeader } from "./elements/WrapHeader";
import { SwapAgainButton } from "pages/Wrap/elements/SwapAgainButton";
import { useTimedFetchTokens } from "../../hooks/useTimedFetchTokens";
import { getTokenMetadata } from "../../store/global/getTokenMetadata";
import { useIsMobile } from "hooks/useDeviceType";
import { TradeAndWrapWrapper } from "wrappers/TradeAndWrapWrapper/TradeAndWrapWrapper";
import { DoubleCTAButtonContainer } from "../../containers/DoubleCTAButtonContainer";

export interface WrapProps {
  takerToken: Token;
  makerToken: Token;
}

export const Wrap: React.FC = () => {
  /**
   * Auto-fetch tokens while the app is running
   * This is for /Trade tokens
   */
  useTimedFetchTokens();
  const networkName = GlobalStore.useState(s => s.network.name);
  getTokenMetadata.useBeckon(networkName);
  const wrapStatus = GlobalStore.useState(s => s.wrapStatus);
  const wrapQuote = GlobalStore.useState(s => s.wrapQuote);
  const network = GlobalStore.useState(s => s.network);
  const isMobile = useIsMobile();

  useUpdateAddressWithENS();
  const takerToken =
    !wrapQuote || wrapQuote.type === "Wrapping"
      ? (network.nativeCurrency.symbol as Token)
      : getWrappedSymbolForChain(network);
  const makerToken =
    !wrapQuote || wrapQuote.type === "Wrapping"
      ? getWrappedSymbolForChain(network)
      : (network.nativeCurrency.symbol as Token);

  const HEADER_TEXT_MAP: Record<WrapStatus, string> = {
    [WrapStatus.ENTRY]: !wrapQuote || wrapQuote.type === "Wrapping" ? "Wrap" : "Unwrap",
    [WrapStatus.REVIEW]: "Review Order",
    [WrapStatus.IN_PROGRESS]: "In Progress",
    [WrapStatus.COMPLETED]: `${
      !wrapQuote || wrapQuote.type === "Wrapping" ? "Wrap" : "Unwrap"
    } Completed`,
    [WrapStatus.CONFIRMING]: "Review Order",
    [WrapStatus.CANCELLED]: "CANCELLED",
    [WrapStatus.ERROR]: "ERROR",
  };

  const PROGRESS_BAR_STEP: Record<WrapStatus, number> = {
    [WrapStatus.ENTRY]: 1,
    [WrapStatus.REVIEW]: 2,
    [WrapStatus.CONFIRMING]: 2,
    [WrapStatus.IN_PROGRESS]: 3,
    [WrapStatus.COMPLETED]: 4,
    [WrapStatus.CANCELLED]: 5,
    [WrapStatus.ERROR]: 5,
  };

  return (
    <TradeAndWrapWrapper data-testid="wrap-modal" isMobile={isMobile}>
      {wrapStatus !== WrapStatus.ENTRY && (
        <WrapHeader title={HEADER_TEXT_MAP[wrapStatus]} wrapStatus={wrapStatus} />
      )}
      {wrapStatus === WrapStatus.ENTRY && <NetworkDropdown />}
      {wrapStatus === WrapStatus.ENTRY && (
        <WrapEntry takerToken={takerToken} makerToken={makerToken} />
      )}
      {wrapStatus === WrapStatus.REVIEW && (
        <WrapReview takerToken={takerToken} makerToken={makerToken} />
      )}
      {wrapStatus === WrapStatus.CONFIRMING && (
        <WrapReview takerToken={takerToken} makerToken={makerToken} />
      )}
      {wrapStatus === WrapStatus.IN_PROGRESS && (
        <WrapInProgress takerToken={takerToken} makerToken={makerToken} />
      )}
      {wrapStatus === WrapStatus.COMPLETED && (
        <WrapCompleted takerToken={takerToken} makerToken={makerToken} />
      )}
      <div className="sp10"></div>
      {wrapStatus === WrapStatus.ENTRY && wrapQuote?.warning && (
        <WarningSection message={wrapQuote.warning} />
      )}
      <ProgressBar step={PROGRESS_BAR_STEP[wrapStatus]} />
      <Spacer minHeight="10px" />
      <DoubleCTAButtonContainer>
        <WrapActionButton />
        {wrapStatus === WrapStatus.COMPLETED && <SwapAgainButton />}
      </DoubleCTAButtonContainer>
    </TradeAndWrapWrapper>
  );
};
